import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useUpdateProviderCredit, type ProviderCredit } from "@/features/admin";
import { useState } from "react";
import { toast } from "sonner";
import {
  badgeTone,
  money,
  SOURCE_LABEL,
  STATUS_LABEL,
  creditStatusTone,
  when,
} from "./credit-format";

/**
 * One vendor on the credits panel.
 *
 * The editable part is the manual balance — the only possible source for Mistral, and the
 * fallback whenever automation has nothing to say — and the alert threshold. Saving a manual
 * balance re-arms alerting for that provider: the entry is a fresh statement about the
 * account, so it should be allowed to alert again immediately if it is still too low.
 */
export function ProviderCreditCard({ credit }: { credit: ProviderCredit }) {
  const update = useUpdateProviderCredit();
  const [balance, setBalance] = useState("");
  const [threshold, setThreshold] = useState(String(credit.threshold));

  const save = async (input: {
    balance?: number | null;
    threshold?: number;
  }) => {
    try {
      await update.mutateAsync({ provider: credit.provider, ...input });
      toast.success(`${credit.label} mis à jour`);
      if (input.balance !== undefined) setBalance("");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Enregistrement impossible",
      );
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{credit.label}</CardTitle>
          <Badge color={badgeTone(credit.status)}>
            {STATUS_LABEL[credit.status]}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div
            className={`text-2xl font-bold ${
              creditStatusTone(credit.status) === "bad" ? "text-danger" : ""
            }`}
          >
            {money(credit)}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Seuil {credit.threshold} $ · {SOURCE_LABEL[credit.source]} ·{" "}
            {when(credit.checked_at)}
            {credit.stale_days !== undefined && credit.stale_days > 0
              ? ` (il y a ${credit.stale_days} j)`
              : ""}
          </div>
        </div>

        {credit.detail ? (
          <p className="text-xs text-muted-foreground">{credit.detail}</p>
        ) : null}

        {credit.error ? (
          <p className="text-xs text-warning-foreground">
            Relevé impossible : {credit.error}
            {credit.balance !== null
              ? " — le solde affiché est le dernier connu."
              : ""}
          </p>
        ) : null}

        {credit.last_alert_at ? (
          <p className="text-xs text-muted-foreground">
            Dernière alerte Sentry : {when(credit.last_alert_at)}
          </p>
        ) : null}

        <div className="flex flex-wrap items-end gap-2 border-t pt-3">
          <label className="flex-1 min-w-[140px] text-xs text-muted-foreground">
            Solde manuel ($)
            <Input
              className="mt-1"
              inputMode="decimal"
              placeholder={
                credit.source === "manual" && credit.balance !== null
                  ? String(credit.balance)
                  : "—"
              }
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
            />
          </label>
          <Button
            variant="outline"
            disabled={update.isPending || !balance.trim()}
            onClick={() => save({ balance: Number(balance.replace(",", ".")) })}
          >
            Enregistrer
          </Button>
          {credit.source === "manual" ? (
            <Button
              variant="ghost"
              disabled={update.isPending}
              onClick={() => save({ balance: null })}
            >
              Effacer
            </Button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[140px] text-xs text-muted-foreground">
            Seuil d'alerte ($)
            <Input
              className="mt-1"
              inputMode="decimal"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
            />
          </label>
          <Button
            variant="outline"
            disabled={update.isPending || Number(threshold) === credit.threshold}
            onClick={() =>
              save({ threshold: Number(threshold.replace(",", ".")) })
            }
          >
            Appliquer
          </Button>
        </div>

        {!credit.automatic ? (
          <p className="text-xs text-muted-foreground">
            Mistral ne publie aucune API de facturation : ce solde ne peut venir
            que d'une saisie manuelle, relevée sur la console Mistral.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
